/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { getHistoricoNsirMetrics } from './constants';

const formatPct = (done, total) => {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
};

const VOLUME_COPY = {
  servicos: {
    suaDigitacao: 'Serviços que você digitou no período selecionado (26→25)',
    suaLabel: 'Sua digitação',
    suaParteOk: 'Sua parte ok',
    nsirLabel: 'NSIR completa',
    foraPeriodo: 'Serviços digitados em outras datas — clique para ver',
    seusUnit: 'seus',
  },
  postes: {
    suaDigitacao: 'Postes que você digitou no período selecionado (26→25)',
    suaLabel: 'Seus postes',
    suaParteOk: 'Sua parte ok',
    nsirLabel: 'NSIR completa',
    foraPeriodo: 'Postes digitados em outras datas — clique para ver',
    seusUnit: 'seus postes',
  },
};

const progressSx = { height: 4, borderRadius: 1 };

const ForaPeriodoChip = ({ count, title, onClick }) => (
  <Tooltip title={title}>
    <Chip
      label={`+${count} fora do período`}
      size="small"
      variant="outlined"
      color="warning"
      onClick={onClick}
      sx={{ mt: 0.25, alignSelf: 'flex-start', cursor: 'pointer', fontWeight: 600 }}
    />
  </Tooltip>
);

ForaPeriodoChip.propTypes = {
  count: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/** Conclusão quieta — maioria das linhas do histórico. */
const CompactComplete = ({
  totalServicosUsuario,
  nsirDone,
  totalServicosNsir,
  copy,
  digitadosForaPeriodo,
  onForaClick,
}) => {
  const tooltip = [
    `Você digitou ${totalServicosUsuario} no período`,
    `NSIR ${nsirDone}/${totalServicosNsir || '—'}`,
  ].join(' · ');

  return (
    <>
      <Tooltip title={tooltip}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ cursor: 'help', minWidth: 0 }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: 18, flexShrink: 0 }}
          />
          <Typography variant="caption" color="success.main" fontWeight={600} noWrap>
            Concluída
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            · {totalServicosUsuario} {copy.seusUnit}
          </Typography>
        </Stack>
      </Tooltip>
      {digitadosForaPeriodo > 0 && (
        <ForaPeriodoChip
          count={digitadosForaPeriodo}
          title={copy.foraPeriodo}
          onClick={onForaClick}
        />
      )}
    </>
  );
};

CompactComplete.propTypes = {
  totalServicosUsuario: PropTypes.number.isRequired,
  nsirDone: PropTypes.number.isRequired,
  totalServicosNsir: PropTypes.number.isRequired,
  copy: PropTypes.object.isRequired,
  digitadosForaPeriodo: PropTypes.number.isRequired,
  onForaClick: PropTypes.func.isRequired,
};

const HistoricoDigitacaoStatusCell = ({ row, onForaPeriodoClick, volumeUnit }) => {
  const metrics = getHistoricoNsirMetrics(row, volumeUnit);
  const copy = VOLUME_COPY[volumeUnit] ?? VOLUME_COPY.servicos;
  const {
    totalServicosNsir,
    pendentesNsir,
    totalServicosUsuario,
    pendentesUsuario,
    digitadosForaPeriodo,
  } = metrics;

  const usuarioTotal = totalServicosUsuario + pendentesUsuario;
  const usuarioPct = formatPct(totalServicosUsuario, usuarioTotal || totalServicosNsir);
  const nsirDone = Math.max(0, totalServicosNsir - pendentesNsir);
  const nsirPct = formatPct(nsirDone, totalServicosNsir);
  const nsirComplete = totalServicosNsir > 0 && pendentesNsir === 0;
  const usuarioComplete = pendentesUsuario === 0 && totalServicosUsuario > 0;
  /** Compacto só quando NSIR fechada e há volume seu no período (sem pendência sua). */
  const isFullyComplete = nsirComplete && usuarioComplete;

  const handleForaClick = (event) => {
    event.stopPropagation();
    if (digitadosForaPeriodo > 0 && onForaPeriodoClick) {
      onForaPeriodoClick(row);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        minWidth: 0,
        width: '100%',
        gap: 0.25,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {isFullyComplete ? (
        <CompactComplete
          totalServicosUsuario={totalServicosUsuario}
          nsirDone={nsirDone}
          totalServicosNsir={totalServicosNsir}
          copy={copy}
          digitadosForaPeriodo={digitadosForaPeriodo}
          onForaClick={handleForaClick}
        />
      ) : (
        <>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
            <Tooltip title={copy.suaDigitacao}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ cursor: 'help' }}>
                {usuarioComplete ? copy.suaParteOk : copy.suaLabel}
              </Typography>
            </Tooltip>
            <Typography variant="caption" noWrap>
              {usuarioComplete
                ? totalServicosUsuario
                : `${totalServicosUsuario}/${usuarioTotal || totalServicosNsir || '—'}`}
              {pendentesUsuario > 0 ? ` · ${pendentesUsuario} pend.` : ''}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={usuarioPct}
            color={usuarioComplete ? 'success' : 'primary'}
            sx={progressSx}
          />
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0.25 }}
          >
            <Tooltip title="Todos os serviços da NSIR — inclui o que outros digitadores fizeram">
              <Typography
                variant="caption"
                color={pendentesNsir > 0 ? 'text.primary' : 'text.secondary'}
                fontWeight={pendentesNsir > 0 ? 600 : 400}
                noWrap
                sx={{ cursor: 'help' }}
              >
                {copy.nsirLabel}
              </Typography>
            </Tooltip>
            <Typography
              variant="caption"
              fontWeight={pendentesNsir > 0 ? 600 : 400}
              noWrap
            >
              {nsirDone}/{totalServicosNsir || '—'}
              {nsirComplete ? ' ✓' : pendentesNsir > 0 ? ` · ${pendentesNsir} pend.` : ''}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={nsirPct}
            color={nsirComplete ? 'success' : 'warning'}
            sx={progressSx}
          />
          {digitadosForaPeriodo > 0 && (
            <ForaPeriodoChip
              count={digitadosForaPeriodo}
              title={copy.foraPeriodo}
              onClick={handleForaClick}
            />
          )}
        </>
      )}
    </Box>
  );
};

HistoricoDigitacaoStatusCell.propTypes = {
  row: PropTypes.object.isRequired,
  onForaPeriodoClick: PropTypes.func,
  volumeUnit: PropTypes.oneOf(['servicos', 'postes']),
};

HistoricoDigitacaoStatusCell.defaultProps = {
  onForaPeriodoClick: undefined,
  volumeUnit: 'servicos',
};

export default HistoricoDigitacaoStatusCell;
